import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import HomePage from './pages/HomePage';
import StatsPage from './pages/StatsPage';
import ProfilePage from './pages/ProfilePage';
import FailedTxGraveyard from './pages/FailedTxGraveyard';
import AdminPage from './pages/AdminPage';
import ArcadePage from './pages/ArcadePage';
import ArcadeLeaderboardPage from './pages/ArcadeLeaderboardPage';
import FairLaunchPage from './pages/FairLaunchPage';
import LorePage from './pages/LorePage';
import DocsPage from './pages/DocsPage';
import AirdropPage from './pages/AirdropPage';
import SeasonsPage from './pages/SeasonsPage';
import AboutPage from './pages/AboutPage';
import AdminAccess from './pages/AdminAccess';
import ChancellorPage from './pages/ChancellorPage';
import CourtPage from './pages/CourtPage';
import WatchedSoulsPage from './pages/WatchedSoulsPage';
import CodexPage from './pages/CodexPage';
import HallsHubPage from './pages/HallsHubPage';
import GhostHallPage from './pages/GhostHallPage';
import SoulProfilePage from './pages/SoulProfilePage';
import SeasonSettlementPage from './pages/SeasonSettlementPage';
import CodexAnalyticsPage from './pages/CodexAnalyticsPage';
import ApiDocsPage from './pages/ApiDocsPage';
import LoadingScreen from './components/LoadingScreen';
import CursorTrail from './components/CursorTrail';
import PageTransition from './components/PageTransition';
import MobileMenu from './components/MobileMenu';
import DemoBanner from './components/DemoBanner';
import DevnetBadge from './components/DevnetBadge';
import SwipeBack from './components/SwipeBack';
import PreviewGate from './components/PreviewGate';
import { JudgesTourPill, JudgesTourController } from './components/JudgesTour';
import { WalletContextProvider } from './context/WalletContext';
import { WalletStateProvider, useWalletState } from './context/WalletStateContext';
import { DemoProvider } from './context/DemoContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// ===========================================
// SECURITY: Inject API key into all fetch requests to our backend
// ===========================================
const GFG_API_KEY = process.env.REACT_APP_GFG_API_KEY;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (GFG_API_KEY && BACKEND_URL) {
  const originalFetch = window.fetch;
  window.fetch = function(url, options = {}) {
    const urlStr = typeof url === 'string' ? url : url?.url || '';
    if (urlStr.startsWith(BACKEND_URL) || urlStr.startsWith('/api')) {
      options.headers = {
        ...(options.headers || {}),
        'X-GFG-API-Key': GFG_API_KEY,
      };
    }
    return originalFetch.call(this, url, options);
  };
}

import { trackEvent } from './utils/trackEvent';

// Syncs Solana wallet adapter state with WalletStateContext
const WalletSync = () => {
  const { connected, publicKey } = useWallet();
  const { setIsWalletConnected, setWalletAddress } = useWalletState();
  
  useEffect(() => {
    setIsWalletConnected(connected);
    setWalletAddress(publicKey ? publicKey.toBase58() : null);
  }, [connected, publicKey, setIsWalletConnected, setWalletAddress]);
  
  return null;
};

// Track page views on route change
const PageViewTracker = () => {
  const { pathname } = useLocation();
  const { publicKey } = useWallet();
  
  useEffect(() => {
    trackEvent('page_view', pathname, publicKey?.toBase58() || null);
  }, [pathname, publicKey]);
  
  return null;
};

// Track wallet connections
const WalletTracker = () => {
  const { connected, publicKey } = useWallet();
  const [wasConnected, setWasConnected] = useState(false);
  
  useEffect(() => {
    if (connected && !wasConnected && publicKey) {
      trackEvent('wallet_connect', null, publicKey.toBase58());
    }
    setWasConnected(connected);
  }, [connected, publicKey, wasConnected]);
  
  return null;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Animated Routes component for page transitions
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <>
      <ScrollToTop />
      <PageViewTracker />
      <WalletTracker />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
        <Route path="/" element={
          <PageTransition>
            <HomePage />
          </PageTransition>
        } />
        <Route path="/stats" element={
          <PageTransition>
            <StatsPage />
          </PageTransition>
        } />
        <Route path="/profile" element={
          <PageTransition>
            <ProfilePage />
          </PageTransition>
        } />
        <Route path="/tokenomics" element={
          <PageTransition>
            <FairLaunchPage />
          </PageTransition>
        } />
        <Route path="/fair-launch" element={
          <PageTransition>
            <FairLaunchPage />
          </PageTransition>
        } />
        <Route path="/lore" element={
          <PageTransition>
            <LorePage />
          </PageTransition>
        } />
        <Route path="/graveyard" element={
          <PageTransition>
            <FailedTxGraveyard />
          </PageTransition>
        } />
        <Route path="/arcade" element={
          <PageTransition>
            <ArcadePage />
          </PageTransition>
        } />
        <Route path="/arcade/leaderboard" element={
          <PageTransition>
            <ArcadeLeaderboardPage />
          </PageTransition>
        } />
        <Route path="/docs" element={
          <PageTransition>
            <DocsPage />
          </PageTransition>
        } />
        <Route path="/admin" element={
          <PageTransition>
            <AdminPage />
          </PageTransition>
        } />
        <Route path="/airdrop" element={
          <PageTransition>
            <AirdropPage />
          </PageTransition>
        } />
        <Route path="/seasons" element={
          <PageTransition>
            <SeasonsPage />
          </PageTransition>
        } />
        <Route path="/about" element={
          <PageTransition>
            <AboutPage />
          </PageTransition>
        } />
        <Route path="/admin-access" element={<AdminAccess />} />
        <Route path="/chancellor" element={
          <PageTransition>
            <ChancellorPage />
          </PageTransition>
        } />
        <Route path="/court" element={
          <PageTransition>
            <CourtPage />
          </PageTransition>
        } />
        <Route path="/feed" element={
          <PageTransition>
            <WatchedSoulsPage />
          </PageTransition>
        } />
        <Route path="/court/codex" element={
          <PageTransition>
            <CodexPage />
          </PageTransition>
        } />
        <Route path="/halls" element={
          <PageTransition>
            <HallsHubPage />
          </PageTransition>
        } />
        <Route path="/chancellor/halls" element={
          <PageTransition>
            <HallsHubPage />
          </PageTransition>
        } />
        <Route path="/ghosts" element={
          <PageTransition>
            <GhostHallPage />
          </PageTransition>
        } />
        <Route path="/soul" element={
          <PageTransition>
            <SoulProfilePage />
          </PageTransition>
        } />
        <Route path="/soul/:address" element={
          <PageTransition>
            <SoulProfilePage />
          </PageTransition>
        } />
        <Route path="/admin/settle" element={
          <PageTransition>
            <SeasonSettlementPage />
          </PageTransition>
        } />
        <Route path="/codex/analytics" element={
          <PageTransition>
            <CodexAnalyticsPage />
          </PageTransition>
        } />
        <Route path="/codex/api" element={
          <PageTransition>
            <ApiDocsPage />
          </PageTransition>
        } />
      </Routes>
    </AnimatePresence>
    </>
  );
};

// PREVIEW_MODE — while true, the JudgesTour is suppressed so visitors only
// see the PreviewGate splash, and on-chain UI (DevnetBadge, LiveMode CTAs)
// is hidden across the app.
const PREVIEW_MODE = process.env.REACT_APP_PREVIEW_MODE === 'true';

// Inner app content
const AppContent = () => {
  return (
    <BrowserRouter>
      <PreviewGate />
      <DemoBanner />
      <DevnetBadge />
      {!PREVIEW_MODE && <JudgesTourPill />}
      {!PREVIEW_MODE && <JudgesTourController />}
      <div data-game-hide><MobileMenu /></div>
      <div data-game-hide><SwipeBack /></div>
      <AnimatedRoutes />
      <div data-game-hide><CursorTrail /></div>
    </BrowserRouter>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(() => {
    // Skip splash for admin-access route
    if (window.location.pathname === '/admin-access') return false;
    return sessionStorage.getItem('gfg_loaded') !== 'true';
  });

  useEffect(() => {
    document.documentElement.classList.add('dark');

    const blockContext = (e) => {
      if (e.target.tagName === 'IMG') e.preventDefault();
    };
    const blockDrag = (e) => {
      if (e.target.tagName === 'IMG') e.preventDefault();
    };
    document.addEventListener('contextmenu', blockContext);
    document.addEventListener('dragstart', blockDrag);
    return () => {
      document.removeEventListener('contextmenu', blockContext);
      document.removeEventListener('dragstart', blockDrag);
    };
  }, []);

  const handleLoadComplete = () => {
    setIsLoading(false);
    sessionStorage.setItem('gfg_loaded', 'true');
  };

  return (
    <div className="App overflow-x-clip">
      <WalletContextProvider>
        <WalletStateProvider>
          <DemoProvider>
          {isLoading ? (
            <LoadingScreen onLoadComplete={handleLoadComplete} />
          ) : (
            <>
              <WalletSync />
              <AppContent />
              <Toaster position="top-center" />
            </>
          )}
          </DemoProvider>
        </WalletStateProvider>
      </WalletContextProvider>
    </div>
  );
}

export default App;
